export type PortvilleApplicationDisplayStatusKey =
  | 'pending'
  | 'checked'
  | 'approved'
  | 'application_cancelled'
  | 'occupancy_cancelled'
  | 'contracted'
  | 'completed'
  | 'unknown';

export type PortvilleApplicationFilterKey =
  | 'all'
  | Exclude<PortvilleApplicationDisplayStatusKey, 'unknown'>;

interface PortvilleApplicationStatusSource {
  status?: unknown;
  consumedReason?: unknown;
}

export interface PortvilleApplicationDisplayStatus {
  key: PortvilleApplicationDisplayStatusKey;
  label: string;
}

const normalizeValue = (value: unknown): string => {
  const withoutControlCharacters = Array.from(String(value ?? ''))
    .filter((character) => {
      const code = character.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('');

  return withoutControlCharacters
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim()
    .toLowerCase();
};

export const getPortvilleApplicationDisplayStatus = (
  application: PortvilleApplicationStatusSource
): PortvilleApplicationDisplayStatus => {
  const status = normalizeValue(application.status);

  switch (status) {
    case 'pending':
      return { key: 'pending', label: '대기중' };
    case 'checked':
      return { key: 'checked', label: '검토완료' };
    case 'approved':
      return { key: 'approved', label: '입주진행' };
    case 'rejected':
      return { key: 'application_cancelled', label: '신청취소' };
    case 'consumed': {
      const consumedReason = normalizeValue(application.consumedReason);

      if (consumedReason === 'contracted') {
        return { key: 'contracted', label: '계약완료' };
      }
      if (consumedReason === 'lottery_cancelled_by_admin') {
        return { key: 'occupancy_cancelled', label: '입주취소(관리자)' };
      }
      if (consumedReason === 'contract_cancelled') {
        return { key: 'occupancy_cancelled', label: '입주취소' };
      }

      // 기존 consumed 문서 중 종료 사유가 없거나 알 수 없는 값인 경우를 안전하게 분리한다.
      return { key: 'completed', label: '처리완료' };
    }
    default:
      return { key: 'unknown', label: `알수없음(${status || 'empty'})` };
  }
};

export const matchesPortvilleApplicationFilter = (
  application: PortvilleApplicationStatusSource,
  filter: PortvilleApplicationFilterKey
): boolean => {
  if (filter === 'all') return true;
  return getPortvilleApplicationDisplayStatus(application).key === filter;
};
